import { db } from '@/lib/firebase';
import {
  collection,
  getDocs,
  setDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  updateDoc,
  writeBatch,
  type Unsubscribe,
} from 'firebase/firestore';

export interface FavoriteItem {
  id: string;           // Firestore doc id
  userId: string;
  itemId: string;       // ID of the favorited item (track, release, artist, etc.)
  itemType: 'track' | 'release' | 'artist' | 'video' | 'news' | 'item' | 'playlist' | 'radio' | 'user' | 'chat';
  title: string;
  subtitle?: string;    // artist name, category, etc.
  imageUrl?: string;    // thumbnail
  audioUrl?: string;    // for audio tracks — so we can play directly from favorites
  videoUrl?: string;    // for direct video files
  youtubeId?: string;   // for YouTube videos — so we can embed in media player
  link?: string;        // route to navigate to
  order?: number;       // display order (lower = first)
  createdAt: Timestamp;
}

/**
 * Add an item to the user's favorites
 */
export async function addFavorite(
  userId: string,
  item: Omit<FavoriteItem, 'id' | 'userId' | 'createdAt'>
): Promise<string> {
  const favRef = doc(collection(db, 'favorites'));
  await setDoc(favRef, {
    userId,
    itemId: item.itemId,
    itemType: item.itemType,
    title: item.title,
    subtitle: item.subtitle || '',
    imageUrl: item.imageUrl || '',
    audioUrl: item.audioUrl || '',
    videoUrl: item.videoUrl || '',
    youtubeId: item.youtubeId || '',
    link: item.link || '',
    createdAt: Timestamp.now(),
  });
  return favRef.id;
}

/**
 * Remove a favorite by its Firestore doc ID
 */
export async function removeFavorite(favoriteDocId: string): Promise<void> {
  await deleteDoc(doc(db, 'favorites', favoriteDocId));
}

/**
 * Remove a favorite by userId + itemId combo
 */
export async function removeFavoriteByItemId(userId: string, itemId: string): Promise<void> {
  const q = query(
    collection(db, 'favorites'),
    where('userId', '==', userId),
    where('itemId', '==', itemId)
  );
  const snap = await getDocs(q);
  for (const d of snap.docs) {
    await deleteDoc(d.ref);
  }
}

/**
 * Get all favorites for a user, ordered newest first
 */
export async function getFavorites(userId: string): Promise<FavoriteItem[]> {
  try {
    // Try composite index query first (requires userId + createdAt index)
    const q = query(
      collection(db, 'favorites'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as FavoriteItem));
  } catch (error: any) {
    // Fallback: query without orderBy and sort client-side (missing composite index)
    if (error?.code === 'failed-precondition' || error?.message?.includes('index')) {
      console.warn('Favorites composite index not available, sorting client-side');
      try {
        const fallbackQ = query(
          collection(db, 'favorites'),
          where('userId', '==', userId)
        );
        const snap = await getDocs(fallbackQ);
        const items = snap.docs.map(d => ({ id: d.id, ...d.data() } as FavoriteItem));
        return items.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });
      } catch (fallbackError) {
        console.error('Error fetching favorites (fallback):', fallbackError);
        return [];
      }
    }
    console.error('Error fetching favorites:', error);
    return [];
  }
}

/**
 * Check if a specific item is favorited by a user
 */
export async function isFavorited(userId: string, itemId: string): Promise<string | null> {
  try {
    const q = query(
      collection(db, 'favorites'),
      where('userId', '==', userId),
      where('itemId', '==', itemId)
    );
    const snap = await getDocs(q);
    return snap.empty ? null : snap.docs[0].id;
  } catch {
    return null;
  }
}

/**
 * Subscribe to a user's favorites in real-time
 * Returns an unsubscribe function
 */
export function subscribeFavorites(
  userId: string,
  callback: (favorites: FavoriteItem[]) => void
): Unsubscribe {
  // Try with orderBy first; fall back to without if composite index is missing
  try {
    const q = query(
      collection(db, 'favorites'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, { includeMetadataChanges: true }, (snap) => {
      // An empty local cache means pending, not an empty favorites account.
      if (snap.empty && snap.metadata.fromCache) return;
      const favs = snap.docs.map(d => ({ id: d.id, ...d.data() } as FavoriteItem));
      favs.sort((a, b) => {
        if (a.order != null && b.order != null) return a.order - b.order;
        // Items without order (newly added) come first (leftmost), sorted by createdAt desc
        if (a.order != null && b.order == null) return 1;
        if (a.order == null && b.order != null) return -1;
        // Both have no order: newest first (createdAt desc from query)
        return 0;
      });
      callback(favs);
    }, (error) => {
      // If index error, fall back to unordered query
      if (error?.code === 'failed-precondition' || error?.message?.includes('index')) {
        console.warn('Favorites composite index not available, using fallback subscription');
        const fallbackQ = query(
          collection(db, 'favorites'),
          where('userId', '==', userId)
        );
        onSnapshot(fallbackQ, { includeMetadataChanges: true }, (snap) => {
          if (snap.empty && snap.metadata.fromCache) return;
          const favs = snap.docs.map(d => ({ id: d.id, ...d.data() } as FavoriteItem));
          favs.sort((a, b) => {
            if (a.order != null && b.order != null) return a.order - b.order;
            // Items without order (newly added) come first (leftmost)
            if (a.order != null && b.order == null) return 1;
            if (a.order == null && b.order != null) return -1;
            const aTime = a.createdAt?.toMillis?.() || 0;
            const bTime = b.createdAt?.toMillis?.() || 0;
            return bTime - aTime;
          });
          callback(favs);
        }, () => callback([]));
      } else {
        console.error('Error subscribing to favorites:', error);
        callback([]);
      }
    });
  } catch {
    // Fallback
    const fallbackQ = query(
      collection(db, 'favorites'),
      where('userId', '==', userId)
    );
    return onSnapshot(fallbackQ, (snap) => {
      const favs = snap.docs.map(d => ({ id: d.id, ...d.data() } as FavoriteItem));
      favs.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return bTime - aTime;
      });
      callback(favs);
    }, () => callback([]));
  }
}

/**
 * Toggle a favorite: add if not present, remove if present
 * Returns { added: boolean, favoriteId?: string }
 */
export async function toggleFavorite(
  userId: string,
  item: Omit<FavoriteItem, 'id' | 'userId' | 'createdAt'>
): Promise<{ added: boolean; favoriteId?: string }> {
  const existingId = await isFavorited(userId, item.itemId);
  if (existingId) {
    await removeFavorite(existingId);
    return { added: false };
  } else {
    const newId = await addFavorite(userId, item);
    return { added: true, favoriteId: newId };
  }
}

/**
 * Reorder favorites by writing order field to each doc
 */
export async function reorderFavorites(orderedIds: string[]): Promise<void> {
  try {
    const batch = writeBatch(db);
    orderedIds.forEach((id, index) => {
      batch.update(doc(db, 'favorites', id), { order: index });
    });
    await batch.commit();
  } catch (error) {
    console.error('Error reordering favorites:', error);
    throw error;
  }
}
